import type { ErrorCodes } from 'noddi-ui-web';
import { NoddiButton, NoddiInfoBanner } from 'noddi-ui-web';
import { LanguageCodeType } from 'noddi-util';
import { useState } from 'react';
import type { PasswordResetOtpConfirmTranslations } from './PasswordResetOtpConfirm';
import { PasswordResetOtpConfirm } from './PasswordResetOtpConfirm';
import { PasswordResetRequest } from './PasswordResetRequest';
import { ResetCountdown } from './ResetCountdown';

type ResetMethod = 'email' | 'sms';

export type PasswordResetFlowTranslations = {
  resetPassword: string;
  enterEmailToReset: string;
  enterPhoneToReset?: string;
  sendResetLink: string;
  cancel: string;
  resetViaEmail?: string;
  resetViaSMS?: string;
  email?: string;
  phoneNumber?: string;
  invalidEmail?: string;
  resetLinkSent: string;
  checkEmailForLink: (email: string) => string;
  linkExpiresIn: string;
  linkExpired: string;
  passwordResetSuccessful: string;
  passwordResetSuccessfulMessage: string;
  backToLogin: string;
  otp?: PasswordResetOtpConfirmTranslations;
};

type PasswordResetFlowProps = {
  /** Gets the address the reset link went to; nothing after an SMS reset. */
  onSuccess: (sentToEmail?: string) => void;
  onCancel: () => void;
  /** Email already entered on the sign-in form. Prefills reset via email. */
  initialEmail?: string;
  enableSmsReset?: boolean;
  translations?: PasswordResetFlowTranslations;
  language?: LanguageCodeType;
  errorCodes?: ErrorCodes;
};

type FlowStep = 'request' | 'emailSent' | 'otp' | 'done';

export const PasswordResetFlow = ({
  onSuccess,
  onCancel,
  initialEmail,
  enableSmsReset,
  translations,
  language,
  errorCodes
}: PasswordResetFlowProps) => {
  const [step, setStep] = useState<FlowStep>('request');
  const [sessionId, setSessionId] = useState<string>();
  const [expiresInSeconds, setExpiresInSeconds] = useState<number>();
  const [sentToEmail, setSentToEmail] = useState('');

  const texts = translations ?? {
    resetPassword: 'Reset password',
    enterEmailToReset: 'Enter your email address to receive a password reset link',
    enterPhoneToReset: 'Enter your phone number to receive a password reset code',
    sendResetLink: 'Send reset link',
    cancel: 'Cancel',
    resetViaEmail: 'Reset via email',
    resetViaSMS: 'Reset via SMS',
    resetLinkSent: 'Reset link sent!',
    checkEmailForLink: (email) =>
      `We've sent a link to reset your password to ${email}. If it doesn't arrive within a few minutes, check your spam folder.`,
    linkExpiresIn: 'The link expires in',
    linkExpired: 'The link has expired. Request a new one to continue.',
    passwordResetSuccessful: 'Password reset successful',
    passwordResetSuccessfulMessage: 'Password reset successful! You can now sign in with your new password.',
    backToLogin: 'Back to login'
  };

  const handleRequestSuccess = (
    resetMethod: ResetMethod,
    options?: { sessionId?: string; expiresInSeconds?: number; email?: string }
  ) => {
    setExpiresInSeconds(options?.expiresInSeconds);
    setSentToEmail(options?.email ?? '');
    if (resetMethod === 'sms' && options?.sessionId) {
      setSessionId(options.sessionId);
      setStep('otp');
    } else {
      setStep('emailSent');
    }
  };

  if (step === 'emailSent') {
    return (
      <div className='flex flex-col gap-4'>
        <h2 className='text-lg font-semibold'>{texts.resetLinkSent}</h2>
        <NoddiInfoBanner variant='success' texts={[{ value: texts.checkEmailForLink(sentToEmail) }]} />
        {expiresInSeconds !== undefined && (
          <ResetCountdown seconds={expiresInSeconds} label={texts.linkExpiresIn} expiredLabel={texts.linkExpired} />
        )}
        <NoddiButton onClick={() => onSuccess(sentToEmail)} className='w-full'>
          {texts.backToLogin}
        </NoddiButton>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className='flex flex-col gap-4'>
        <h2 className='text-lg font-semibold'>{texts.passwordResetSuccessful}</h2>
        <NoddiInfoBanner variant='success' texts={[{ value: texts.passwordResetSuccessfulMessage }]} />
        <NoddiButton onClick={() => onSuccess()} className='w-full'>
          {texts.backToLogin}
        </NoddiButton>
      </div>
    );
  }

  if (step === 'otp' && sessionId) {
    return (
      <PasswordResetOtpConfirm
        sessionId={sessionId}
        expiresInSeconds={expiresInSeconds}
        onSuccess={() => setStep('done')}
        onCancel={onCancel}
        translations={texts.otp}
        errorCodes={errorCodes}
      />
    );
  }

  return (
    <PasswordResetRequest
      initialEmail={initialEmail}
      enableSmsReset={enableSmsReset}
      onSuccess={handleRequestSuccess}
      onCancel={onCancel}
      translations={texts}
      language={language}
      errorCodes={errorCodes}
    />
  );
};
